import { type FormEvent, startTransition, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import type { DocumentKind } from "@/features/status/status-domain";
import type {
  AnalysisResponse,
  ExtractAcceptedResponse,
  LlmProvider,
  PolizzaExtractAcceptedResponse,
  PolizzaExtractResponse,
} from "@/lib/api/extract";
import { buildPdfWithSelectedPages, getPdfPageCount } from "@/lib/pdf";
import { PdfPagePreviewSelector } from "./PdfPagePreviewSelector";
import { DEFAULT_PDF_RENDER_DPI, DOCUMENT_KIND_OPTIONS } from "./upload-config";

type UploadSubmitPayload = {
  file: File;
  documentKind: DocumentKind;
  async: boolean;
  pdfRenderDpi: number;
  codesLlmProvider: LlmProvider | null;
};

type UploadTabProps = {
  onSubmitExtract: (
    payload: UploadSubmitPayload,
  ) => Promise<
    | AnalysisResponse
    | ExtractAcceptedResponse
    | PolizzaExtractResponse
    | PolizzaExtractAcceptedResponse
  >;
  onSubmitted: () => void;
  busy: boolean;
  hasActiveRuns: boolean;
  errorMessage: string | null;
  onResetError: () => void;
};

export function UploadTabPlaceholder({
  onSubmitExtract,
  onSubmitted,
  busy,
  hasActiveRuns,
  errorMessage,
  onResetError,
}: UploadTabProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfLoadRequestRef = useRef(0);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [documentKind, setDocumentKind] = useState<DocumentKind>("fatture_ddt");
  const [pdfRenderDpi, setPdfRenderDpi] = useState(DEFAULT_PDF_RENDER_DPI);
  const [codesLlmProvider, setCodesLlmProvider] = useState<LlmProvider | "">(
    "ollama",
  );
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [selectedPageIndices, setSelectedPageIndices] = useState<number[]>([]);
  const [pageSelectorError, setPageSelectorError] = useState<string | null>(
    null,
  );
  const [isReadingPdf, setIsReadingPdf] = useState(false);
  const [isPreparingPdf, setIsPreparingPdf] = useState(false);
  const [preparationError, setPreparationError] = useState<string | null>(null);

  const isUploadDisabled =
    busy || hasActiveRuns || isReadingPdf || isPreparingPdf;

  const selectedFileLabel = file
    ? `${file.name} (${Math.max(1, Math.round(file.size / 1024))} KB)`
    : "Nessun file selezionato.";
  const selectedPagesCount = selectedPageIndices.length;
  const areAllPagesSelected =
    pageCount !== null && selectedPagesCount === pageCount;

  const selectedDocumentOption = DOCUMENT_KIND_OPTIONS.find(
    (item) => item.id === documentKind,
  );
  const documentHelp = selectedDocumentOption?.helpText ?? "";
  const documentUploadLabel =
    documentKind === "fatture_ddt"
      ? "Carica PDF fattura o DDT"
      : "Carica PDF polizza di carico";
  const documentUploadHelp =
    documentKind === "fatture_ddt"
      ? "Seleziona il PDF di fattura o DDT da inviare in analisi."
      : "Seleziona il PDF della polizza di carico da inviare in analisi.";
  const documentSubmitLabel =
    documentKind === "fatture_ddt"
      ? "Invia fattura/DDT"
      : "Invia polizza di carico";

  function setDragging(nextValue: boolean) {
    setIsDragging(nextValue);
  }

  async function loadPdfPageCount(candidate: File) {
    const requestId = pdfLoadRequestRef.current + 1;
    pdfLoadRequestRef.current = requestId;
    setIsReadingPdf(true);
    setPageSelectorError(null);
    setPageCount(null);
    setSelectedPageIndices([]);

    try {
      const totalPages = await getPdfPageCount(candidate);
      if (pdfLoadRequestRef.current !== requestId) return;

      const initialSelection = Array.from(
        { length: totalPages },
        (_, pageIndex) => pageIndex,
      );
      setPageCount(totalPages);
      setSelectedPageIndices(initialSelection);
    } catch {
      if (pdfLoadRequestRef.current !== requestId) return;
      setFile(null);
      setPageCount(null);
      setSelectedPageIndices([]);
      setPageSelectorError(
        "Impossibile leggere le pagine del PDF selezionato. Verifica che il file sia valido.",
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } finally {
      if (pdfLoadRequestRef.current === requestId) {
        setIsReadingPdf(false);
      }
    }
  }

  function selectPdf(candidate: File | null) {
    if (!candidate) return;
    const isPdf =
      candidate.type === "application/pdf" ||
      candidate.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) return;
    onResetError();
    setPageSelectorError(null);
    setPreparationError(null);
    setFile(candidate);
    void loadPdfPageCount(candidate);
  }

  function clearSelectedPdf() {
    pdfLoadRequestRef.current += 1;
    onResetError();
    setFile(null);
    setPageCount(null);
    setSelectedPageIndices([]);
    setPageSelectorError(null);
    setPreparationError(null);
    setIsReadingPdf(false);
    setIsPreparingPdf(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function handleDocumentKindChange(nextKind: DocumentKind) {
    if (nextKind === documentKind) return;
    clearSelectedPdf();
    setDocumentKind(nextKind);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !file ||
      isUploadDisabled ||
      pageCount === null ||
      selectedPageIndices.length === 0
    ) {
      return;
    }

    let uploadFile = file;
    setPreparationError(null);

    if (!areAllPagesSelected) {
      try {
        setIsPreparingPdf(true);
        uploadFile = await buildPdfWithSelectedPages(file, selectedPageIndices);
      } catch {
        setPreparationError(
          "Impossibile preparare il PDF con le pagine selezionate.",
        );
        setIsPreparingPdf(false);
        return;
      }
    }

    try {
      setIsPreparingPdf(true);
      await onSubmitExtract({
        file: uploadFile,
        documentKind,
        async: true,
        pdfRenderDpi,
        codesLlmProvider: codesLlmProvider || null,
      });
      clearSelectedPdf();
      onSubmitted();
    } catch {
      // Error message is already set in parent state.
    } finally {
      setIsPreparingPdf(false);
    }
  }

  function handleSelectedPageIndicesChange(nextPageIndices: number[]) {
    startTransition(() => {
      setSelectedPageIndices(nextPageIndices);
    });
  }

  return (
    <section className="panel-screen">
      <header className="panel-screen__header">
        <h2 className="panel-screen__title">Carica un PDF per l'analisi</h2>
        <p className="panel-screen__description">
          Scegli il workflow di analisi e carica il PDF nel riquadro del tipo
          selezionato.
        </p>
      </header>
      <div className="panel-screen__body">
        {hasActiveRuns && !busy ? (
          <div
            className="upload-form__error"
            role="alert"
            style={{ marginBottom: "1.5rem" }}
          >
            Attenzione: sono presenti altre analisi in corso nel sistema.
            Attendi il completamento prima di caricare nuovi documenti.
          </div>
        ) : null}
        <form className="upload-form" onSubmit={handleSubmit}>
          <div className="upload-form__field">
            <span className="upload-form__label">Tipo documento</span>
            <div className="upload-form__kind-grid">
              {DOCUMENT_KIND_OPTIONS.map((option) => {
                const isActive = documentKind === option.id;

                if (!isActive) {
                  return (
                    <button
                      key={option.id}
                      type="button"
                      aria-pressed="false"
                      className="upload-kind-card upload-kind-card--inactive"
                      onClick={() => handleDocumentKindChange(option.id)}
                      disabled={isUploadDisabled}
                    >
                      <span className="upload-kind-card__header">
                        <span className="upload-kind-card__title">
                          {option.title}
                        </span>
                      </span>
                      <span className="upload-kind-card__copy">
                        {option.description}
                      </span>
                    </button>
                  );
                }

                return (
                  <div
                    key={option.id}
                    className="upload-kind-card upload-kind-card--active"
                  >
                    <div className="upload-kind-card__summary">
                      <span className="upload-kind-card__header">
                        <span className="upload-kind-card__title">
                          {option.title}
                        </span>
                        <span className="upload-kind-card__badge">
                          Workflow attivo
                        </span>
                      </span>
                      <span className="upload-kind-card__copy">
                        {option.description}
                      </span>
                    </div>
                    <div className="upload-kind-card__active-panel">
                      <span className="upload-kind-card__active-title">
                        {documentUploadLabel}
                      </span>
                      <span className="upload-kind-card__active-copy">
                        {documentUploadHelp}
                      </span>
                      <input
                        id="pdf-upload-input"
                        name="pdfDocument"
                        ref={fileInputRef}
                        className="upload-form__native-file-input"
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={(event) =>
                          selectPdf(event.target.files?.[0] ?? null)
                        }
                        disabled={isUploadDisabled}
                        aria-label={documentUploadLabel}
                      />
                      <label
                        htmlFor="pdf-upload-input"
                        className={`upload-form__picker-row ${isDragging ? "upload-form__picker-row--dragging" : ""}`}
                        onDragEnter={(event) => {
                          event.preventDefault();
                          setDragging(true);
                        }}
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDragging(true);
                        }}
                        onDragLeave={(event) => {
                          event.preventDefault();
                          setDragging(false);
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          setDragging(false);
                          selectPdf(event.dataTransfer.files?.[0] ?? null);
                        }}
                      >
                        <Button
                          type="button"
                          variant="outline"
                          className="upload-form__pick-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                          disabled={isUploadDisabled}
                        >
                          Seleziona PDF
                        </Button>
                        <div className="upload-form__picker-copy">
                          <span className="upload-form__picker-text">
                            {selectedFileLabel}
                          </span>
                          <span className="upload-form__picker-help">
                            Oppure trascina qui il PDF
                          </span>
                        </div>
                        {file ? (
                          <Button
                            type="button"
                            variant="ghost"
                            className="upload-form__remove-button"
                            onClick={(event) => {
                              event.stopPropagation();
                              clearSelectedPdf();
                            }}
                            disabled={isUploadDisabled}
                          >
                            Rimuovi
                          </Button>
                        ) : null}
                      </label>
                      {file ? (
                        <PdfPagePreviewSelector
                          file={file}
                          isBusy={isUploadDisabled}
                          isReadingPdf={isReadingPdf}
                          onChangeSelectedPageIndices={
                            handleSelectedPageIndicesChange
                          }
                          pageCount={pageCount}
                          pdfLoadErrorMessage={pageSelectorError}
                          selectedPageIndices={selectedPageIndices}
                          key={`${file.name}-${file.lastModified}-${file.size}`}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="upload-form__hint">{documentHelp}</p>
          </div>

          <div className="upload-form__field upload-form__field--inline">
            <div className="upload-form__slider-header">
              <label className="upload-form__label" htmlFor="pdf-render-dpi">
                Qualita render PDF
              </label>
              <output
                className="upload-form__slider-value"
                htmlFor="pdf-render-dpi"
              >
                {pdfRenderDpi} DPI
              </output>
            </div>
            <input
              id="pdf-render-dpi"
              className="upload-form__slider"
              type="range"
              min="150"
              max="300"
              step="10"
              value={pdfRenderDpi}
              onChange={(event) =>
                setPdfRenderDpi(
                  Number(event.target.value) || DEFAULT_PDF_RENDER_DPI,
                )
              }
              disabled={isUploadDisabled}
            />
            <div className="upload-form__slider-scale" aria-hidden="true">
              <span className="upload-form__slider-tick">150</span>
              <span className="upload-form__slider-tick upload-form__slider-tick--quarter">
                200
              </span>
              <span className="upload-form__slider-tick upload-form__slider-tick--three-quarters">
                250
              </span>
              <span className="upload-form__slider-tick upload-form__slider-tick--end">
                300
              </span>
            </div>
            <p className="upload-form__hint">
              Aumentare i DPI migliora i dettagli dell&apos;estrazione, ma puo
              richiedere piu tempo di elaborazione.
            </p>
          </div>

          <div className="upload-form__field upload-form__field--inline">
            <label className="upload-form__label" htmlFor="codes-llm-provider">
              Provider AI
            </label>
            <select
              id="codes-llm-provider"
              name="codesLlmProvider"
              className="upload-form__input"
              value={codesLlmProvider}
              onChange={(event) => {
                const nextProvider = event.target.value as LlmProvider | "";
                setCodesLlmProvider(nextProvider);
              }}
              disabled={isUploadDisabled}
            >
              <option value="ollama">Privato</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>

          <Button
            className="upload-form__submit"
            type="submit"
            disabled={
              !file ||
              isUploadDisabled ||
              pageCount === null ||
              selectedPagesCount === 0
            }
          >
            {busy
              ? "Invio in corso…"
              : isPreparingPdf
                ? "Preparazione PDF…"
                : documentSubmitLabel}
          </Button>
        </form>
        {errorMessage ? (
          <p className="upload-form__error" role="alert">
            Errore: {errorMessage}
          </p>
        ) : null}
        {preparationError ? (
          <p className="upload-form__error" role="alert">
            Errore: {preparationError}
          </p>
        ) : null}
      </div>
    </section>
  );
}
